import { Box, Button, CircularProgress, Container, Paper, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import BottomBar from "../../components/BottomBar";
import TopBar from "../../components/TopBar";
import { getPurchaseReceipt, getPurchaseTracking } from "../../util/api/paymentApi";
import { getPurchaseReceiptAccess } from "../../util/purchaseReceiptAccess";
import { trackPurchase } from "../../util/metaPixel";

const POLL_DELAY = 2000;
const MAX_ATTEMPTS = 15;
const textStyle = { fontSize: "0.95rem", lineHeight: 1.65 };
const buttonStyle = { background: "#f9f5ee", color: "#15151a", "&:hover": { background: "#fff" } };

const LicenseCard = ({ license, accountLinked }) => {
  const [copyStatus, setCopyStatus] = useState("");
  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(license.license_key);
      setCopyStatus("License key copied.");
    } catch {
      setCopyStatus("Select the license key above and copy it manually.");
    }
  };

  return <Paper component="section" elevation={0} sx={{ bgcolor: "#202126", color: "#f9f5ee", border: "1px solid #414249", borderRadius: 3, p: { xs: 2.5, sm: 4 } }}>
    <Typography component="h2" sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" }, fontWeight: 700, mb: 2 }}>{license.product_name}</Typography>
    <Typography component="p" sx={{ fontSize: "0.75rem", color: "#bfc0c8", mb: 1 }}>Your license key</Typography>
    <Box component="code" aria-label={`${license.product_name} license key`} sx={{ display: "block", bgcolor: "#111216", borderRadius: 1.5, p: 2, fontSize: { xs: "0.85rem", sm: "1rem" }, overflowWrap: "anywhere", userSelect: "all", letterSpacing: "0.03em" }}>
      {license.license_key}
    </Box>
    <Button startIcon={<ContentCopyIcon />} onClick={copyKey} sx={{ color: "#f9f5ee", mt: 1, textTransform: "none" }}>Copy license key</Button>
    {copyStatus && <Typography role="status" sx={{ fontSize: "0.8rem", mt: 0.5 }}>{copyStatus}</Typography>}
    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 2.5 }}>
      {!accountLinked && <Button href={`/claim?key=${encodeURIComponent(license.license_key)}`} target="_blank" rel="noopener noreferrer" variant="contained" sx={buttonStyle}>Claim license</Button>}
      {license.downloads.map(download => <Box key={download.product_id} sx={{ width: license.downloads.length > 1 ? "100%" : "auto" }}>
        {license.downloads.length > 1 && <Typography sx={{ ...textStyle, mb: 1 }}>{download.product_name}</Typography>}
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Button href={download.windows_download_url} target="_blank" rel="noopener noreferrer" variant="outlined" startIcon={<DownloadIcon />} sx={{ color: "#f9f5ee", borderColor: "#878891" }}>Download Windows</Button>
          <Button href={download.mac_download_url} target="_blank" rel="noopener noreferrer" variant="outlined" startIcon={<DownloadIcon />} sx={{ color: "#f9f5ee", borderColor: "#878891" }}>Download Mac</Button>
        </Box>
      </Box>)}
    </Box>
  </Paper>;
};

const PaymentSuccess = () => {
  const { sessionID } = useParams();
  const [receipt, setReceipt] = useState(null);
  const [state, setState] = useState("loading");
  const [retry, setRetry] = useState(0);
  const checkoutSessionID = getPurchaseReceiptAccess(sessionID);

  useEffect(() => {
    let active = true;
    let stopTracking = () => {};
    getPurchaseTracking(sessionID).then((result) => {
      if (active && result.data) stopTracking = trackPurchase(result.data);
    }).catch(() => {
      // Analytics availability does not affect a completed checkout.
    });
    return () => { active = false; stopTracking(); };
  }, [sessionID]);

  useEffect(() => {
    let active = true;
    let timer;
    let attempts = 0;
    setReceipt(null);
    if (!checkoutSessionID) {
      setState("unavailable");
      return;
    }
    setState("loading");
    const load = async () => {
      if (!active) return;
      try {
        const { data } = await getPurchaseReceipt(sessionID, checkoutSessionID);
        if (!active) return;
        if (data.status === "ready") {
          setReceipt(data);
          setState("ready");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) setState("pending");
        else timer = setTimeout(load, POLL_DELAY);
      } catch {
        if (active) setState("error");
      }
    };
    load();
    return () => { active = false; clearTimeout(timer); };
  }, [sessionID, checkoutSessionID, retry]);

  return <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "linear-gradient(145deg, #f5f4f1, #d9dadd 55%, #b8bac0)" }}>
    <Helmet>
      <title>WaveMind Audio | Your Purchase</title>
      <meta name="robots" content="noindex, nofollow" />
      <meta name="referrer" content="no-referrer" />
    </Helmet>
    <TopBar />
    <Container component="main" maxWidth="md" sx={{ flex: 1, py: { xs: 5, md: 8 }, color: "#191a20" }}>
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 44, mb: 1 }} />
        <Typography component="h1" sx={{ fontSize: { xs: "1.8rem", sm: "2.5rem" }, fontWeight: 700, mb: 1 }}>Enjoy your new amp sim!</Typography>
        <Typography sx={textStyle}>Thank you for your purchase. Your license key will also be sent to your email.</Typography>
      </Box>

      {state === "loading" && <Box role="status" sx={{ textAlign: "center", py: 4 }}>
        <CircularProgress size={28} sx={{ color: "#191a20", mb: 2 }} />
        <Typography sx={textStyle}>Preparing your license. This can take a moment after payment.</Typography>
      </Box>}
      {(state === "pending" || state === "error" || state === "unavailable") && <Paper elevation={0} sx={{ bgcolor: "rgba(255,255,255,0.65)", color: "#191a20", borderRadius: 3, p: 3, textAlign: "center", mb: 3 }}>
        <Typography role="status" sx={textStyle}>
          {state === "pending" ? "Your license is still being prepared. Check again shortly, or look for your license email."
            : state === "unavailable" ? "Open the original checkout return link to view your license here. You can also find your key in your purchase email."
              : "We could not load your license right now. Please try again. Your license will still be sent by email."}
        </Typography>
        {state !== "unavailable" && <Button onClick={() => setRetry(value => value + 1)} variant="contained" sx={{ ...buttonStyle, mt: 2 }}>Check again</Button>}
        <Button href="/support" sx={{ color: "#191a20", mt: 2, mx: 1 }}>Contact support</Button>
      </Paper>}

      {receipt && <>
        {receipt.account_linked && <Typography role="status" sx={{ ...textStyle, textAlign: "center", mb: 3 }}>Your license has already been added to the account you used at checkout.</Typography>}
        <Box sx={{ display: "grid", gap: 2.5 }}>
          {receipt.licenses.map(license => <LicenseCard key={license.license_key} license={license} accountLinked={receipt.account_linked} />)}
        </Box>
        <Box component="section" sx={{ mt: 4, px: { xs: 0.5, sm: 2 } }}>
          <Typography component="h2" sx={{ fontSize: "1.25rem", fontWeight: 700, mb: 2 }}>Get started</Typography>
          <Box component="ol" sx={{ pl: 2.5, m: 0, "& li": { pl: 0.5, mb: 1.5, ...textStyle } }}>
            {!receipt.account_linked && <li>Select <strong>Claim license</strong> above. Sign in or create a free WaveMind account, then select <strong>Claim</strong> to add the prefilled key to your account.</li>}
            <li>Download the Mac or Windows installer for your plugin above and run it.</li>
            <li>Open the plugin in your DAW or launch the standalone app, then sign in with your WaveMind account to activate your license.</li>
          </Box>
        </Box>
      </>}
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <Button href="/account#licenses" variant="contained" sx={buttonStyle}>Go to Licenses</Button>
      </Box>
    </Container>
    <BottomBar />
  </Box>;
};

export default PaymentSuccess;
